import type React from 'react';

import { Box, Card } from '@mui/material';
import {
  FiqlSearch,
  ListPagination,
  OrdDatagridSingleRowSelection,
  OrdListHeader,
} from '@ragu-components/ord-components';
import { ListBase, ResourceContextProvider, useListContext } from 'react-admin';

import type { SxProps, Theme } from '@mui/material';
import type { RowClickFunction, Identifier, FilterPayload, RaRecord } from 'react-admin';

interface ResourceListProps {
  resource: string;
  selectedResourceId: Identifier | null;
  onRowClick: RowClickFunction;
  children: React.ReactNode;
  filter?: FilterPayload | undefined;
  cardSx?: SxProps<Theme>;
}

const ResourceListContent = ({
  resource,
  selectedResourceId,
  onRowClick,
  children,
  cardSx,
}: ResourceListProps): React.ReactElement => {
  const { data: listData } = useListContext();
  const handleToggleItem = (id: Identifier): void => {
    const listRecords = (listData ?? []) as RaRecord[];
    const record = listRecords.find((item) => item.id === id);
    if (record === undefined) {
      return;
    }
    void onRowClick(id, resource, record);
  };
  return (
    <Card
      sx={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        minHeight: 0,
        ...cardSx,
      }}
    >
      <OrdListHeader hasCreate={false} title='' showTabTitle={false} hasExport={false} hasCollapse={false} icon={<></>}>
        <FiqlSearch hasOwnFormContext={false} />
      </OrdListHeader>
      <Box
        sx={{
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        <OrdDatagridSingleRowSelection
          resource={resource}
          selectedId={selectedResourceId ?? undefined}
          onToggleItem={handleToggleItem}
          isRowSelectable={() => true}
        >
          {children}
        </OrdDatagridSingleRowSelection>
      </Box>
      <ListPagination />
    </Card>
  );
};

const ResourceList = ({
  resource,
  selectedResourceId,
  onRowClick,
  children,
  filter = undefined,
  cardSx,
}: ResourceListProps): React.ReactElement => {
  return (
    <ResourceContextProvider value={resource}>
      <ListBase disableSyncWithLocation filter={filter} perPage={25}>
        <ResourceListContent
          resource={resource}
          selectedResourceId={selectedResourceId}
          onRowClick={onRowClick}
          cardSx={cardSx}
        >
          {children}
        </ResourceListContent>
      </ListBase>
    </ResourceContextProvider>
  );
};

export default ResourceList;
